import { getEffectiveToday } from '../../../utils/systemTime';
import { resolveAssignmentInstanceLabel } from '../../../utils/assignmentLabels';
// TEACHING_SCHEDULE_ASSIGNMENT_FIXES_20260916
import { useState, useEffect } from 'react';
import { useStudentDataRefreshTick } from '../../../utils/studentDataRefresh';
import { getNativeDeviceToken } from '../../../utils/nativeDeviceAuth';
import {
  applyStudentAssignmentTiming,
  getStudentAssignmentDate
} from '../../../utils/assignmentTiming';
import {
  expandStudentAssignmentScheduleOccurrences,
  loadStudentAssignmentScheduleContext
} from '../../../utils/studentAssignmentSchedule';
import {
  listStudentHonorsEvents
} from '../../../utils/honorsEvents';
import {
  loadStudentStructuredFormCheckpoints
} from '../../../utils/structuredFormStudentTiming';


const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*,course_week_content(description)&order=week_number.asc`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchStudentAttendanceSchedule(
  enrolmentId
) {
  const empty = {
    enabledWeekIds: [],
    timeOverrides: {},
    roomOverrides: {}
  };

  if (!enrolmentId) {
    return empty;
  }

  const membershipResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_memberships?enrolment_id=eq.${enrolmentId}&select=attendance_group_id&limit=1`,
      {
        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );


  if (
    !membershipResponse.ok
  ) {
    return empty;
  }


  const memberships =
    await membershipResponse
      .json();


  const groupId =
    memberships?.[0]
      ?.attendance_group_id ||
    null;


  if (!groupId) {
    return empty;
  }


  const scheduleResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/attendance_group_weeks?attendance_group_id=eq.${groupId}&select=course_week_id,start_time_override,classroom_override`,
      {
        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );


  if (
    !scheduleResponse.ok
  ) {
    return empty;
  }


  const rows =
    await scheduleResponse
      .json();


  const validRows =
    Array.isArray(rows)
      ? rows.filter(
          row =>
            row
              .course_week_id
        )
      : [];


  return {
    enabledWeekIds:
      validRows.map(
        row =>
          String(
            row.course_week_id
          )
      ),

    timeOverrides:
      Object.fromEntries(
        validRows
          .filter(
            row =>
              row
                .start_time_override
          )
          .map(
            row => [
              String(
                row.course_week_id
              ),

              String(
                row.start_time_override
              ).slice(
                0,
                5
              )
            ]
          )
      ),

    roomOverrides:
      Object.fromEntries(
        validRows
          .filter(
            row =>
              String(
                row
                  .classroom_override ||
                ''
              ).trim()
          )
          .map(
            row => [
              String(
                row.course_week_id
              ),

              String(
                row.classroom_override
              ).trim()
            ]
          )
      )
  };
}

async function fetchHolidays(
  instructorId
) {
  if (!instructorId) {
    return [];
  }

  const instructorResponse =
    await fetch(
      `${SUPABASE_URL}/rest/v1/instructors?id=eq.${instructorId}&select=institution_id&limit=1`,
      {
        headers: {
          apikey: SUPABASE_KEY,
          Authorization:
            'Bearer ' + SUPABASE_KEY
        }
      }
    );

  const instructorRows =
    await instructorResponse.json();

  const institutionId =
    instructorRows?.[0]
      ?.institution_id ||
    null;

  if (!institutionId) {
    return [];
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/holidays?institution_id=eq.${institutionId}&or=(is_personal.eq.false,instructor_id.eq.${instructorId})&select=*&order=date.asc`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchMeetings(
  courseId,
  studentId
) {
  if (
    !courseId ||
    !studentId
  ) {
    return [];
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/student_meetings?course_id=eq.${courseId}&student_meeting_slots.student_id=eq.${studentId}&select=id,course_id,meeting_type,meeting_date,instructor_id,location,instructor:instructors(name),student_meeting_slots!inner(id,meeting_time,student_id)&order=meeting_date.asc`,
      {
        headers: {
          apikey:
            SUPABASE_KEY,
          Authorization:
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  return response.json();
}


async function fetchInstructorAppointments(
  session,
  studentId,
  courseId
) {
  if (
    !session ||
    !studentId ||
    !courseId
  ) {
    return [];
  }

  let deviceToken =
    null;

  if (
    session
      ?.nativeDeviceSession ===
    true
  ) {
    deviceToken =
      await getNativeDeviceToken();
  }

  const webToken =
    String(
      session
        ?.webSessionToken ||
      ''
    ).trim() ||
    null;

  if (
    !deviceToken &&
    !webToken
  ) {
    return [];
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/student_get_instructor_appointments`,
      {
        method:
          'POST',

        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            p_student_id:
              studentId,

            p_device_token:
              deviceToken,

            p_web_token:
              webToken,

            p_course_id:
              courseId
          })
      }
    );

  const result =
    await response.json();

  if (
    !response.ok ||
    result?.ok === false
  ) {
    console.error(
      'Could not load instructor meetings:',
      result
    );

    return [];
  }

  return Array.isArray(
    result?.appointments
  )
    ? result.appointments
    : [];
}


async function fetchCourseAssignments(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(name,requires_submission,assignment_kind)&order=instance_number.asc`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchStructuredFormCheckpoints({
  courseId,
  enrolment,
  assignments,
  assignmentTypeSettings,
  isMaster
}) {
  return loadStudentStructuredFormCheckpoints({
    courseId,
    enrolment,
    assignments,
    assignmentTypeSettings,
    isMaster
  });
}


async function fetchAssignmentTypeSettings(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=course_id,assignment_type_id,display_name,group_deadlines_enabled,sequence_id,schedule_enabled,schedule_choice_enabled,schedule_open_offset_days,schedule_due_offset_days,schedule_choice_open_date,schedule_choice_close_date,schedule_choice_required_count`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchStudentAssignmentGroupMemberships(
  enrolmentId
) {
  if (!enrolmentId) {
    return [];
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?enrolment_id=eq.${enrolmentId}&select=course_assignment_id,enrolment_id,group_number`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchStudentAssignmentGroupDeadlines(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_group_deadlines?select=course_assignment_id,group_number,open_date,due_date,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchStudentAssignmentSubmissionTiming(
  enrolmentId
) {
  if (!enrolmentId) {
    return [];
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=eq.${enrolmentId}&select=course_assignment_id,enrolment_id,course_assignment_schedule_item_id,structured_form_instance_id,submitted,drive_file_id,file_name,text,extension_days`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchWeekReadings(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=*,course_readings(*),course_weeks!inner(course_id)&course_weeks.course_id=eq.${courseId}&order=display_order.asc`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


async function fetchCourseEvents(
  courseId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_events?select=*,course_weeks!inner(course_id)&course_weeks.course_id=eq.${courseId}&order=event_date.asc,event_time.asc`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization:
          'Bearer ' + SUPABASE_KEY
      }
    }
  );

  return response.json();
}


function formatDate(
  dateStr
) {
  if (!dateStr) {
    return '';
  }

  return new Date(
    `${String(dateStr).slice(0, 10)}T12:00:00`
  ).toLocaleDateString(
    'en-GB',
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }
  );
}


function hasMeaningfulRichText(
  html
) {
  return String(html || '')
    .replace(
      /<br\s*\/?\s*>/gi,
      ' '
    )
    .replace(
      /<[^>]*>/g,
      ' '
    )
    .replace(
      /&nbsp;|&#160;/gi,
      ' '
    )
    .replace(
      /\s+/g,
      ' '
    )
    .trim()
    .length > 0;
}


function getCalendarWeekStart(
  dateStr
) {
  if (!dateStr) {
    return '';
  }

  const parts =
    String(dateStr)
      .slice(0, 10)
      .split('-')
      .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(
      Number.isFinite
    )
  ) {
    return '';
  }

  const date =
    new Date(
      parts[0],
      parts[1] - 1,
      parts[2]
    );

  const dayOfWeek =
    date.getDay();

  date.setDate(
    date.getDate() -
      (
        dayOfWeek === 0
          ? 6
          : dayOfWeek - 1
      )
  );

  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(
      2,
      '0'
    ),
    String(
      date.getDate()
    ).padStart(
      2,
      '0'
    )
  ].join('-');
}


function getStudentScheduleAssignmentName(
  assignment
) {
  return (
    String(
      assignment
        ?.assignment_display_name ||
      ''
    ).trim() ||
    assignment
      ?.assignment_types
      ?.name ||
    'Assignment'
  );
}

function getStudentScheduleAssignmentLabel(
  assignment,
  baseAssignments
) {
  const sameTypeCount =
    new Set(
      (baseAssignments || [])
        .filter(
          candidate =>
            String(
              candidate.assignment_type_id
            ) ===
            String(
              assignment.assignment_type_id
            )
        )
        .map(
          candidate =>
            String(candidate.id)
        )
    ).size;

  return resolveAssignmentInstanceLabel(
    assignment,
    {
      baseName:
        getStudentScheduleAssignmentName(
          assignment
        ),
      sameTypeCount
    }
  );
}

export default function StudentSchedule({
  session,
  course,
  enrolment,
  hideHeading = false,
  dataOnly = false,
  includeHonorsEvents = true,
  onDataLoaded = null
}) {
  const [weeks, setWeeks] =
    useState([]);

  const [holidays, setHolidays] =
    useState([]);

  const [meetings, setMeetings] =
    useState([]);

  const [
    instructorAppointments,
    setInstructorAppointments
  ] = useState([]);

  const [
    courseAssignments,
    setCourseAssignments
  ] = useState([]);

  const [
    structuredCheckpoints,
    setStructuredCheckpoints
  ] = useState([]);

  const [
    weekReadings,
    setWeekReadings
  ] = useState([]);

  const [
    courseEvents,
    setCourseEvents
  ] = useState([]);

  const [
    relevantEventAssignments,
    setRelevantEventAssignments
  ] = useState({});

  const [
    relevantReadingAssignments,
    setRelevantReadingAssignments
  ] = useState({});

  const [
    honorsEvents,
    setHonorsEvents
  ] = useState([]);

  const [loading, setLoading] =
    useState(true);

  const [
    previewReading,
    setPreviewReading
  ] = useState(null);

  const refreshTick = useStudentDataRefreshTick();


  useEffect(() => {
    let cancelled = false;

    async function loadSchedule() {
      setLoading(true);

      try {
        const [
          w,
          h,
          m,
          ia,
          ca,
          settings,
          memberships,
          deadlines,
          submissions,
          wr,
          ce,
          he,
          attendanceSchedule
        ] = await Promise.all([
          fetchWeeks(
            course.id
          ),

          fetchHolidays(
            course.instructor_id
          ),

          fetchMeetings(
            course.id,
            session.studentId
          ),

          fetchInstructorAppointments(
            session,
            session.studentId,
            course.id
          ),

          fetchCourseAssignments(
            course.id
          ),

          fetchAssignmentTypeSettings(
            course.id
          ),

          fetchStudentAssignmentGroupMemberships(
            enrolment.id
          ),

          fetchStudentAssignmentGroupDeadlines(
            course.id
          ),

          fetchStudentAssignmentSubmissionTiming(
            enrolment.id
          ),

          fetchWeekReadings(
            course.id
          ),

          fetchCourseEvents(
            course.id
          ),

          includeHonorsEvents &&
          !session
            ?.isMaster
            ? listStudentHonorsEvents(
                session
              )
                .catch(
                  error => {
                    console.error(
                      'Loading Honors events for Teaching Schedule failed:',
                      error
                    );

                    return {
                      events: []
                    };
                  }
                )
            : Promise.resolve({
                events: []
              }),

          fetchStudentAttendanceSchedule(
  enrolment.id
)
        ]);

        if (cancelled) {
          return;
        }

        const displayNameByType =
          new Map(
            (
              Array.isArray(
                settings
              )
                ? settings
                : []
            ).map(
              setting => [
                String(
                  setting
                    .assignment_type_id
                ),
                String(
                  setting
                    .display_name ||
                  ''
                ).trim()
              ]
            )
          );

        const namedAssignments =
          (
            Array.isArray(ca)
              ? ca
              : []
          ).map(
            assignment => ({
              ...assignment,
              assignment_display_name:
                displayNameByType.get(
                  String(
                    assignment
                      .assignment_type_id
                  )
                ) ||
                assignment
                  ?.assignment_types
                  ?.name ||
                'Assignment'
            })
          );

        const structuredRows =
          await fetchStructuredFormCheckpoints({
            courseId:
              course.id,
            enrolment,
            assignments:
              namedAssignments,
            assignmentTypeSettings:
              Array.isArray(settings)
                ? settings
                : [],
            isMaster:
              !!session?.isMaster
          });

        if (cancelled) {
          return;
        }


        const baseResolvedAssignments =
          applyStudentAssignmentTiming({
            assignments:
              namedAssignments,

            assignmentTypeSettings:
              Array.isArray(
                settings
              )
                ? settings
                : [],

            enrolments: [
              enrolment
            ],

            memberships:
              Array.isArray(
                memberships
              )
                ? memberships
                : [],

            deadlines:
              Array.isArray(
                deadlines
              )
                ? deadlines
                : [],

            submissions:
              Array.isArray(
                submissions
              )
                ? submissions
                : []
          });


        const scheduleContext =
          await loadStudentAssignmentScheduleContext({
            courseIds: [
              course.id
            ],

            enrolments: [
              {
                ...enrolment,
                course_id:
                  course.id
              }
            ],

            includeChoiceState:
              !session?.isMaster
          });

        if (cancelled) {
          return;
        }


        const resolvedAssignments =
          expandStudentAssignmentScheduleOccurrences({
            assignments:
              baseResolvedAssignments,

            assignmentTypeSettings:
              Array.isArray(
                settings
              )
                ? settings
                : [],

            enrolments: [
              {
                ...enrolment,
                course_id:
                  course.id
              }
            ],

            memberships:
              Array.isArray(
                memberships
              )
                ? memberships
                : [],

            submissions:
              Array.isArray(
                submissions
              )
                ? submissions
                : [],

            scheduleContext,

            isMaster:
              !!session
                ?.isMaster
          });


        const scheduleItemById =
          new Map(
            (
              scheduleContext
                ?.scheduleItems ||
              []
            ).map(
              item => [
                String(item.id),
                item
              ]
            )
          );

        const relevantEvents = {};
        const relevantReadings = {};

        function addRelevantLabel(
          target,
          id,
          label
        ) {
          if (!id) {
            return;
          }

          const key =
            String(id);

          const existing =
            new Set(
              target[key] ||
              []
            );

          existing.add(label);

          target[key] =
            [...existing];
        }

        resolvedAssignments
          .forEach(
            assignment => {
              if (
                !assignment
                  .schedule_occurrence ||
                !assignment
                  .course_assignment_schedule_item_id
              ) {
                return;
              }

              const item =
                scheduleItemById.get(
                  String(
                    assignment
                      .course_assignment_schedule_item_id
                  )
                );

              if (!item) {
                return;
              }

              const label =
                getStudentScheduleAssignmentLabel(
                  assignment,
                  namedAssignments
                );

              if (
                item.item_type ===
                  'event' &&
                item.course_event_id
              ) {
                addRelevantLabel(
                  relevantEvents,
                  item.course_event_id,
                  label
                );
              }

              if (
                item.item_type ===
                  'reading' &&
                item
                  .week_reading_assignment_id
              ) {
                addRelevantLabel(
                  relevantReadings,
                  item
                    .week_reading_assignment_id,
                  label
                );
              }
            }
          );

        structuredRows.forEach(
          checkpoint => {
            if (
              checkpoint.course_event_id
            ) {
              addRelevantLabel(
                relevantEvents,
                checkpoint.course_event_id,
                checkpoint.label
              );
            }

            if (
              checkpoint
                .week_reading_assignment_id
            ) {
              addRelevantLabel(
                relevantReadings,
                checkpoint
                  .week_reading_assignment_id,
                checkpoint.label
              );
            }
          }
        );

        setRelevantEventAssignments(
          relevantEvents
        );

        setRelevantReadingAssignments(
          relevantReadings
        );

        setWeeks(
          Array.isArray(w)
            ? w
            : []
        );

        setHolidays(
          Array.isArray(h)
            ? h
            : []
        );

        setMeetings(
          Array.isArray(m)
            ? m
            : []
        );

        setInstructorAppointments(
          Array.isArray(ia)
            ? ia
            : []
        );

        setCourseAssignments(
          resolvedAssignments
        );

        setStructuredCheckpoints(
          Array.isArray(
            structuredRows
          )
            ? structuredRows
            : []
        );

        setWeekReadings(
          Array.isArray(wr)
            ? wr
            : []
        );

        setCourseEvents(
          Array.isArray(ce)
            ? ce
            : []
        );

        setHonorsEvents(
          (Array.isArray(he?.events) ? he.events : [])
            .filter(event => event?.registration?.status === 'Registered')
        );

        if (typeof onDataLoaded === 'function') {
          onDataLoaded({
            course,
            enrolment,
            weeks: Array.isArray(w) ? w : [],
            holidays: Array.isArray(h) ? h : [],
            meetings: Array.isArray(m) ? m : [],
            instructorAppointments: Array.isArray(ia) ? ia : [],
            assignments: resolvedAssignments,
            assignmentTypeSettings:
              Array.isArray(settings)
                ? settings
                : [],
            structuredCheckpoints: Array.isArray(structuredRows) ? structuredRows : [],
            weekReadings: Array.isArray(wr) ? wr : [],
            courseEvents: Array.isArray(ce) ? ce : [],
            attendanceEnabledWeekIds:
  course
    ?.group_based_attendance ===
    true
    ? (
        Array.isArray(
          attendanceSchedule
            ?.enabledWeekIds
        )
          ? attendanceSchedule
              .enabledWeekIds
          : []
      )
    : (
        Array.isArray(w)
          ? w
              .filter(
                week =>
                  week
                    .attendance_enabled ===
                  true
              )
              .map(
                week =>
                  String(
                    week.id
                  )
              )
          : []
      ),

attendanceTimeOverrides:
  attendanceSchedule
    ?.timeOverrides &&
  typeof attendanceSchedule
    .timeOverrides ===
    'object'
    ? attendanceSchedule
        .timeOverrides
    : {},

attendanceRoomOverrides:
  attendanceSchedule
    ?.roomOverrides &&
  typeof attendanceSchedule
    .roomOverrides ===
    'object'
    ? attendanceSchedule
        .roomOverrides
    : {},
            relevantEventAssignments: relevantEvents,
            relevantReadingAssignments: relevantReadings
          });
        }

      } catch (error) {
        console.error('Loading Teaching Schedule failed:', error);
        if (!cancelled && typeof onDataLoaded === 'function') {
          onDataLoaded({ course, enrolment, error });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }


    loadSchedule();


    return () => {
      cancelled = true;
    };
 }, [
  course,
  enrolment,
  session,
  includeHonorsEvents,
  onDataLoaded,
  refreshTick
]);


  function getHoliday(
    date
  ) {
    return holidays.find(
      holiday =>
        holiday.date ===
        date
    );
  }


  function isFirstActualClassOfWeek(
    week
  ) {
    const weekStart =
      getCalendarWeekStart(
        week.class_date
      );

    const sameWeek =
      weeks
        .filter(
          candidate =>
            getCalendarWeekStart(
              candidate.class_date
            ) === weekStart
        )
        .sort(
          (a, b) =>
            String(
              a.class_date
            ).localeCompare(
              String(
                b.class_date
              )
            )
        );

    const firstActualClass =
      sameWeek.find(
        candidate =>
          !getHoliday(
            candidate.class_date
          )
      ) ||
      sameWeek[0];

    return (
      firstActualClass
        ?.id ===
      week.id
    );
  }


  function getMeetingsForWeek(
    week
  ) {
    const classDate =
      new Date(
        `${week.class_date}T12:00:00`
      );

    const dayOfWeek =
      classDate.getDay();

    const monday =
      new Date(
        classDate
      );

    monday.setDate(
      classDate.getDate() -
        (
          dayOfWeek === 0
            ? 6
            : dayOfWeek - 1
        )
    );

    const sunday =
      new Date(
        monday
      );

    sunday.setDate(
      monday.getDate() + 6
    );

    return meetings.filter(
      meeting => {
        const date =
          new Date(
            `${String(
              meeting
                .meeting_date
            ).slice(
              0,
              10
            )}T12:00:00`
          );

        return (
          date >= monday &&
          date <= sunday
        );
      }
    );
  }


  function getInstructorAppointmentsForWeek(
    week
  ) {
    const weekStart =
      getCalendarWeekStart(
        week.class_date
      );

    return instructorAppointments
      .filter(
        appointment =>
          getCalendarWeekStart(
            appointment
              .meeting_date
          ) ===
          weekStart
      );
  }


  function getAssignmentsDueInWeek(
    week
  ) {
    const weekStart =
      getCalendarWeekStart(
        week.class_date
      );

    return courseAssignments
      .filter(
        assignment => {
          if (
            assignment
              ?.assignment_types
              ?.assignment_kind ===
            'structured_form'
          ) {
            return false;
          }

          const date =
            getStudentAssignmentDate(
              assignment
            );

          return (
            date &&
            getCalendarWeekStart(
              date
            ) ===
            weekStart
          );
        }
      )
      .sort(
        (a, b) => {
          const dateCompare =
            String(
              getStudentAssignmentDate(
                a
              ) ||
              ''
            ).localeCompare(
              String(
                getStudentAssignmentDate(
                  b
                ) ||
                ''
              )
            );

          if (
            dateCompare !== 0
          ) {
            return dateCompare;
          }

          const labelCompare =
            String(
              getStudentScheduleAssignmentLabel(
                a,
                courseAssignments
              ) ||
              ''
            ).localeCompare(
              String(
                getStudentScheduleAssignmentLabel(
                  b,
                  courseAssignments
                ) ||
                ''
              ),
              undefined,
              {
                numeric: true,
                sensitivity:
                  'base'
              }
            );

          if (
            labelCompare !== 0
          ) {
            return labelCompare;
          }

          return String(
            a.student_assignment_occurrence_key ||
            a.id ||
            ''
          ).localeCompare(
            String(
              b.student_assignment_occurrence_key ||
              b.id ||
              ''
            )
          );
        }
      );
  }


  function getStructuredCheckpointsForWeek(
    week
  ) {
    const weekStart =
      getCalendarWeekStart(
        week.class_date
      );

    return structuredCheckpoints
      .filter(
        checkpoint =>
          checkpoint.due_date &&
          getCalendarWeekStart(
            checkpoint.due_date
          ) === weekStart
      )
      .sort(
        (a, b) =>
          String(
            a.due_date ||
            ''
          ).localeCompare(
            String(
              b.due_date ||
              ''
            )
          ) ||
          String(
            a.label ||
            ''
          ).localeCompare(
            String(
              b.label ||
              ''
            )
          )
      );
  }


  function getSelfEvalForWeek(
    week
  ) {
    if (
      course
        .self_eval_enabled !==
      true
    ) {
      return [];
    }

    const weekStart =
      getCalendarWeekStart(
        week.class_date
      );

    const items = [];


    if (
      course
        .mid_eval_close_date &&
      getCalendarWeekStart(
        course
          .mid_eval_close_date
      ) === weekStart
    ) {
      items.push({
        label:
          'Mid-Term Self-Evaluation',

        date:
          course
            .mid_eval_close_date
      });
    }


    if (
      course
        .final_eval_close_date &&
      getCalendarWeekStart(
        course
          .final_eval_close_date
      ) === weekStart
    ) {
      items.push({
        label:
          'Final Self-Evaluation',

        date:
          course
            .final_eval_close_date
      });
    }


    return items;
  }


  function getWeekReadingsList(
    weekId
  ) {
    return weekReadings
      .filter(
        reading =>
          reading
            .course_week_id ===
          weekId
      )
      .sort(
        (a, b) =>
          a.display_order -
          b.display_order
      );
  }


  function getWeekEvents(
    weekId
  ) {
    return courseEvents
      .filter(
        event =>
          event
            .course_week_id ===
          weekId
      )
      .sort(
        (a, b) => {
          const dateCompare =
            String(
              a.event_date ||
              ''
            ).localeCompare(
              String(
                b.event_date ||
                ''
              )
            );

          if (
            dateCompare !== 0
          ) {
            return dateCompare;
          }

          return String(
            a.event_time ||
            ''
          ).localeCompare(
            String(
              b.event_time ||
              ''
            )
          );
        }
      );
  }


  function getHonorsEventsForWeek(
    week
  ) {
    if (
      !isFirstActualClassOfWeek(
        week
      )
    ) {
      return [];
    }

    const weekStart =
      getCalendarWeekStart(
        week.class_date
      );

    return honorsEvents
      .filter(
        event =>
          event.eventDate &&
          getCalendarWeekStart(
            event.eventDate
          ) ===
            weekStart
      )
      .sort(
        (a, b) =>
          String(
            a.eventDate ||
            ''
          ).localeCompare(
            String(
              b.eventDate ||
              ''
            )
          ) ||
          String(
            a.startTime ||
            ''
          ).localeCompare(
            String(
              b.startTime ||
              ''
            )
          )
      );
  }


  const today =
    getEffectiveToday();


  if (dataOnly) return null;

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.muted}>
          Loading...
        </div>
      </div>
    );
  }


  return (
    <div style={styles.wrap}>
      <div style={styles.content}>
        <style>{`
          .week-description p {
            margin: 0;
          }

          .week-description p + p {
            margin-top: 6px;
          }
        `}</style>


        {!hideHeading && (
          <h2 style={styles.heading}>
            Teaching Schedule
          </h2>
        )}


        {previewReading && (
          <div
            style={
              styles.previewOverlay
            }
            onClick={() =>
              setPreviewReading(
                null
              )
            }
          >
            <div
              style={
                styles.previewPanel
              }
              onClick={
                event =>
                  event
                    .stopPropagation()
              }
            >
              <div
                style={
                  styles.previewHeader
                }
              >
                <div>
                  <div
                    style={
                      styles.previewTitle
                    }
                  >
                    {
                      previewReading
                        .title
                    }
                  </div>

                  {previewReading
                    .author && (
                    <div
                      style={
                        styles
                          .previewAuthor
                      }
                    >
                      by {
                        previewReading
                          .author
                      }
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  style={
                    styles.previewClose
                  }
                  onClick={() =>
                    setPreviewReading(
                      null
                    )
                  }
                >
                  ×
                </button>
              </div>


              {previewReading
                .drive_file_id ? (
                <iframe
                  src={
                    `https://drive.google.com/file/d/${previewReading.drive_file_id}/preview`
                  }
                  style={
                    styles.previewFrame
                  }
                  title={
                    previewReading
                      .title
                  }
                />
              ) : (
                <div
                  style={
                    styles
                      .previewNoFile
                  }
                >
                  No file available.
                </div>
              )}


              {previewReading
                .drive_file_id && (
                <a
                  href={
                    `https://drive.google.com/uc?export=download&id=${previewReading.drive_file_id}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  style={
                    styles.downloadLink
                  }
                >
                  Download
                </a>
              )}
            </div>
          </div>
        )}


        <div
          style={
            styles.weekList
          }
        >
          {weeks.map(
            week => {
              const holiday =
                getHoliday(
                  week.class_date
                );

              const weekMeetings =
                getMeetingsForWeek(
                  week
                );

              const instructorMeetings =
                getInstructorAppointmentsForWeek(
                  week
                );

              const dueAssignments =
                isFirstActualClassOfWeek(
                  week
                )
                  ? getAssignmentsDueInWeek(
                      week
                    )
                  : [];

              const structuredDue =
                isFirstActualClassOfWeek(
                  week
                )
                  ? getStructuredCheckpointsForWeek(
                      week
                    )
                  : [];

              const selfEvals =
                getSelfEvalForWeek(
                  week
                );

              const readings =
                getWeekReadingsList(
                  week.id
                );

              const events =
                getWeekEvents(
                  week.id
                );

              const honorsProgramEvents =
                getHonorsEventsForWeek(
                  week
                );

              const description =
                Array.isArray(
                  week
                    .course_week_content
                )
                  ? week
                      .course_week_content
                      [0]
                      ?.description

                  : week
                      .course_week_content
                      ?.description;


              const datedItems = [
                ...(
                  holiday
                    ? [{
                        type:
                          'holiday',

                        date:
                          week
                            .class_date,

                        key:
                          `holiday-${week.id}`,

                        item:
                          holiday
                      }]
                    : []
                ),

                ...weekMeetings.map(
                  meeting => ({
                    type:
                      'meeting',

                    date:
                      meeting
                        .meeting_date,

                    key:
                      `meeting-${meeting.id}`,

                    item:
                      meeting
                  })
                ),

                ...instructorMeetings
                  .map(
                    meeting => ({
                      type:
                        'instructorMeeting',

                      date:
                        meeting
                          .meeting_date,

                      key:
                        `instructor-meeting-${meeting.id}`,

                      item:
                        meeting
                    })
                  ),

                ...events.map(
                  event => ({
                    type:
                      'event',

                    date:
                      event
                        .event_date ||
                      week
                        .class_date,

                    key:
                      `event-${event.id}`,

                    item:
                      event
                  })
                ),

                ...honorsProgramEvents
                  .map(
                    event => ({
                      type:
                        'honorsEvent',

                      date:
                        event
                          .eventDate,

                      key:
                        `honors-event-${event.eventId}`,

                      item:
                        event
                    })
                  ),

                ...dueAssignments.map(
                  assignment => ({
                    type:
                      'assignment',

                    date:
                      getStudentAssignmentDate(
                        assignment
                      ),

                    key:
                      `assignment-${
                        assignment
                          .student_assignment_occurrence_key ||
                        assignment.id
                      }`,

                    item:
                      assignment
                  })
                ),

                ...structuredDue.map(
                  checkpoint => ({
                    type:
                      'structuredCheckpoint',

                    date:
                      checkpoint
                        .due_date,

                    key:
                      `structured-${checkpoint.id}`,

                    item:
                      checkpoint
                  })
                ),

                ...selfEvals.map(
                  (
                    selfEval,
                    index
                  ) => ({
                    type:
                      'selfEval',

                    date:
                      selfEval.date,

                    key:
                      `selfeval-${index}`,

                    item:
                      selfEval
                  })
                )
              ].sort(
                (a, b) =>
                  String(
                    a.date ||
                    ''
                  ).localeCompare(
                    String(
                      b.date ||
                      ''
                    )
                  )
              );


              const isPast =
                week.class_date <
                today;

              const isCurrent =
                week.class_date ===
                today;


              const hasContent =
                Boolean(
                  holiday
                ) ||
                weekMeetings
                  .length > 0 ||
                instructorMeetings
                  .length > 0 ||
                dueAssignments
                  .length > 0 ||
                structuredDue
                  .length > 0 ||
                selfEvals
                  .length > 0 ||
                events
                  .length > 0 ||
                honorsProgramEvents
                  .length > 0 ||
                readings
                  .length > 0 ||
                hasMeaningfulRichText(
                  description
                );


              return (
                <div
                  key={
                    week.id
                  }
                  style={{
                    ...styles
                      .weekCard,

                    ...(
                      isCurrent
                        ? styles
                            .weekCardCurrent
                        : {}
                    ),

                    ...(
                      isPast
                        ? styles
                            .weekCardPast
                        : {}
                    )
                  }}
                >
                  <div
                    style={
                      styles
                        .weekHeader
                    }
                  >
                    <div
                      style={
                        styles
                          .weekNum
                      }
                    >
                      Week {
                        week
                          .week_number
                      }:{' '}

                      <span
                        style={
                          styles
                            .weekDate
                        }
                      >
                        {
                          formatDate(
                            week
                              .class_date
                          )
                        }
                      </span>
                    </div>
                  </div>


                  {hasContent && (
                    <div
                      style={
                        styles
                          .weekBody
                      }
                    >
                      {datedItems.map(
                        entry => {
                          if (
                            entry.type ===
                            'holiday'
                          ) {
                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={
                                  styles
                                    .infoRow
                                }
                              >
                                <span
                                  style={
                                    styles
                                      .infoTag
                                  }
                                >
                                  No Class
                                </span>

                                <span>
                                  {
                                    entry
                                      .item
                                      .reason
                                  }
                                </span>
                              </div>
                            );
                          }


                          if (
                            entry.type ===
                            'instructorMeeting'
                          ) {
                            const meeting =
                              entry.item;

                            const details = [
                              String(
                                meeting
                                  .meeting_time ||
                                ''
                              ).slice(
                                0,
                                5
                              ),

                              meeting
                                .location ||
                              null
                            ].filter(
                              Boolean
                            );


                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={
                                  styles
                                    .infoRow
                                }
                              >
                                <span
                                  style={
                                    styles
                                      .infoTag
                                  }
                                >
                                  Meeting
                                </span>

                                <span>
                                  <strong>
                                    Meeting with {
                                      meeting
                                        .instructor_name
                                    }
                                  </strong>

                                  {' — '}

                                  {
                                    formatDate(
                                      meeting
                                        .meeting_date
                                    )
                                  }

                                  {details.length
                                    ? `, ${details.join(' · ')}`
                                    : ''}

                                  {meeting.topic
                                    ? (
                                      <>
                                        <br />
                                        {
                                          meeting
                                            .topic
                                        }
                                      </>
                                    )
                                    : null}
                                </span>
                              </div>
                            );
                          }


                          if (
                            entry.type ===
                            'meeting'
                          ) {
                            const meeting =
                              entry.item;

                            const slot =
                              Array.isArray(
                                meeting
                                  .student_meeting_slots
                              )
                                ? meeting
                                    .student_meeting_slots[0]
                                : null;

                            const instructorName =
                              meeting
                                ?.instructor
                                ?.name ||
                              null;

                            const details = [
                              slot
                                ?.meeting_time
                                ? String(
                                    slot
                                      .meeting_time
                                  ).slice(
                                    0,
                                    5
                                  )
                                : null,

                              meeting.location ||
                              null
                            ].filter(Boolean);

                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={
                                  styles
                                    .infoRow
                                }
                              >
                                <span
                                  style={
                                    styles
                                      .infoTag
                                  }
                                >
                                  Self-eval
                                </span>

                                <span>
                                  <strong>
                                    {
                                      meeting
                                        .meeting_type
                                    } Self-Evaluation Meeting
                                    {instructorName
                                      ? ` with ${instructorName}`
                                      : ''}
                                  </strong>

                                  {' — '}

                                  {
                                    formatDate(
                                      meeting
                                        .meeting_date
                                    )
                                  }

                                  {details.length
                                    ? `, ${details.join(' · ')}`
                                    : ''}
                                </span>
                              </div>
                            );
                          }


                          if (
                            entry.type ===
                            'event'
                          ) {
                            const event =
                              entry.item;

                            const relevantLabels =
                              relevantEventAssignments[
                                String(
                                  event.id
                                )
                              ] ||
                              [];

                            const isRelevant =
                              relevantLabels.length >
                              0;

                            const details = [
                              event
                                .event_time
                                ? String(
                                    event
                                      .event_time
                                  ).slice(
                                    0,
                                    5
                                  )
                                : null,

                              event
                                .location ||
                              null
                            ].filter(
                              Boolean
                            );


                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={{
                                  ...styles
                                    .infoRow,
                                  ...(
                                    isRelevant
                                      ? styles
                                          .assignmentLinkedRow
                                      : {}
                                  )
                                }}
                              >
                                <span
                                  style={
                                    styles
                                      .infoTag
                                  }
                                >
                                  Event
                                </span>

                                <span>
                                  <strong>
                                    {
                                      event.title
                                    }
                                  </strong>

                                  {event
                                    .event_date
                                    ? ` — ${formatDate(event.event_date)}`
                                    : ''}

                                  {details.length
                                    ? `${event.event_date ? ', ' : ' — '}${details.join(' · ')}`
                                    : ''}

                                  {isRelevant && (
                                    <span
                                      style={
                                        styles
                                          .assignmentLinkedNote
                                      }
                                    >
                                      {' '}
                                      · Relevant to:{' '}
                                      {relevantLabels.join(
                                        ', '
                                      )}
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          }


                          if (
                            entry.type ===
                            'honorsEvent'
                          ) {
                            const event =
                              entry.item;

                            const details = [
                              event
                                .startTime ||
                              null,

                              event
                                .location ||
                              null
                            ].filter(
                              Boolean
                            );


                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={
                                  styles
                                    .infoRow
                                }
                              >
                                <span
                                  style={
                                    styles
                                      .infoTag
                                  }
                                >
                                  Honors Event
                                </span>

                                <span>
                                  <strong>
                                    {
                                      event.title
                                    }
                                  </strong>

                                  {' — '}

                                  {
                                    formatDate(
                                      event
                                        .eventDate
                                    )
                                  }

                                  {details.length
                                    ? `, ${details.join(' · ')}`
                                    : ''}
                                </span>
                              </div>
                            );
                          }


                          if (
                            entry.type ===
                            'assignment'
                          ) {
                            const assignment =
                              entry.item;

                            const sameTypeAssignmentIds =
                              new Set(
                                courseAssignments
                                  .filter(
                                    candidate =>
                                      candidate
                                        .assignment_type_id ===
                                      assignment
                                        .assignment_type_id
                                  )
                                  .map(
                                    candidate =>
                                      candidate.id
                                  )
                              );

                            const occurrenceSuffix =
                              assignment
                                .schedule_source_title
                                ? ` — ${assignment.schedule_source_title}`
                                : '';

                            const label =
                              resolveAssignmentInstanceLabel(
                                assignment,
                                {
                                  baseName:
                                    getStudentScheduleAssignmentName(
                                      assignment
                                    ),
                                  sameTypeCount:
                                    sameTypeAssignmentIds.size
                                }
                              ) +
                              occurrenceSuffix;

                            const requiresSubmission =
                              assignment
                                .assignment_types
                                ?.requires_submission ??
                              true;


                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={
                                  styles
                                    .infoRow
                                }
                              >
                                {requiresSubmission && (
                                  <span
                                    style={
                                      styles
                                        .infoTag
                                    }
                                  >
                                    Due
                                  </span>
                                )}

                                <span>
                                  {requiresSubmission
                                    ? (
                                      <>
                                        <strong>
                                          {
                                            label
                                          }
                                        </strong>

                                        {' '}due,{' '}

                                        {
                                          formatDate(
                                            getStudentAssignmentDate(
                                              assignment
                                            )
                                          )
                                        }
                                      </>
                                    )
                                    : (
                                      <strong>
                                        {
                                          label
                                        }
                                      </strong>
                                    )}
                                </span>
                              </div>
                            );
                          }


                          if (
                            entry.type ===
                            'structuredCheckpoint'
                          ) {
                            const checkpoint =
                              entry.item;

                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={
                                  styles
                                    .infoRow
                                }
                              >
                                <span
                                  style={
                                    styles
                                      .infoTag
                                  }
                                >
                                  Due
                                </span>

                                <span>
                                  <strong>
                                    {
                                      checkpoint
                                        .label
                                    }
                                  </strong>

                                  {' '}due,{' '}

                                  {
                                    formatDate(
                                      checkpoint
                                        .due_date
                                    )
                                  }

                                  <span
                                    style={{
                                      color:
                                        '#7b8794',
                                      fontSize:
                                        '11px'
                                    }}
                                  >
                                    {' · '}
                                    submission part
                                    {checkpoint
                                      .schedule_source_label
                                      ? ` · ${checkpoint.schedule_source_label}`
                                      : ''}
                                  </span>
                                </span>
                              </div>
                            );
                          }


                          if (
                            entry.type ===
                            'selfEval'
                          ) {
                            const selfEval =
                              entry.item;

                            return (
                              <div
                                key={
                                  entry.key
                                }
                                style={
                                  styles
                                    .infoRow
                                }
                              >
                                <span
                                  style={
                                    styles
                                      .infoTag
                                  }
                                >
                                  Due
                                </span>

                                <span>
                                  <strong>
                                    {
                                      selfEval
                                        .label
                                    }
                                  </strong>

                                  {' '}due,{' '}

                                  {
                                    formatDate(
                                      selfEval
                                        .date
                                    )
                                  }
                                </span>
                              </div>
                            );
                          }


                          return null;
                        }
                      )}


                      {hasMeaningfulRichText(
                        description
                      ) && (
                        <div
                          style={
                            styles
                              .description
                          }
                          className="week-description"
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html:
                                description
                            }}
                          />
                        </div>
                      )}


                      {readings.map(
                        weekReading => {
                          const reading =
                            weekReading
                              .course_readings;

                          const relevantLabels =
                            relevantReadingAssignments[
                              String(
                                weekReading.id
                              )
                            ] ||
                            [];

                          const isRelevant =
                            relevantLabels.length >
                            0;

                          return (
                            <div
                              key={
                                weekReading
                                  .id
                              }
                              style={{
                                ...styles
                                  .infoRow,
                                ...(
                                  isRelevant
                                    ? styles
                                        .assignmentLinkedRow
                                    : {}
                                )
                              }}
                            >
                              <span
                                style={
                                  styles
                                    .infoTag
                                }
                              >
                                Reading
                              </span>

                              <button
                                type="button"
                                style={
                                  styles
                                    .readingTitle
                                }
                                onClick={() =>
                                  setPreviewReading(
                                    reading
                                  )
                                }
                              >
                                {
                                  reading
                                    ?.title
                                }

                                {reading
                                  ?.author
                                  ? ` by ${reading.author}`
                                  : ''}

                                {isRelevant && (
                                  <span
                                    style={
                                      styles
                                        .assignmentLinkedNote
                                    }
                                  >
                                    {' '}
                                    · Relevant to:{' '}
                                    {relevantLabels.join(
                                      ', '
                                    )}
                                  </span>
                                )}
                              </button>
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}


const styles = {
  wrap: {
    minHeight: 0,
    background:
      'transparent'
  },

  content: {
    padding: 0,
    maxWidth: 'none',
    margin: 0
  },

  muted: {
    color: '#627d98',
    fontSize: '14px'
  },

  heading: {
    margin:
      '0 0 20px',
    fontSize: '26px',
    color: '#243b53',
    fontWeight: '700'
  },

  weekList: {
    display: 'flex',
    flexDirection:
      'column',
    gap: '8px'
  },

  weekCard: {
    background:
      'white',
    border:
      '1px solid #d9e2ec',
    borderRadius:
      '6px',
    overflow:
      'hidden'
  },

  weekCardCurrent: {
    border:
      '2px solid #2f6f9f'
  },

  weekCardPast: {
    opacity: 0.75
  },

  weekHeader: {
    padding:
      '14px 16px'
  },

  weekNum: {
    fontWeight: '700',
    fontSize: '13px',
    color: '#2f6f9f',
    marginBottom: '2px'
  },

  weekDate: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#243b53'
  },

  weekBody: {
    padding:
      '0 16px 14px'
  },

  infoRow: {
    display: 'flex',
    alignItems:
      'flex-start',
    gap: '8px',
    padding:
      '7px 10px',
    background:
      '#f7f8fa',
    borderRadius:
      '4px',
    marginBottom:
      '6px',
    fontSize: '14px',
    color: '#243b53'
  },

  assignmentLinkedRow: {
    background:
      '#fff8e6',
    border:
      '1px solid #e6b94f',
    boxShadow:
      '0 0 0 1px rgba(230, 185, 79, 0.08)'
  },

  assignmentLinkedNote: {
    color:
      '#8a5b00',
    fontSize:
      '12px',
    fontWeight:
      '700'
  },

  infoTag: {
    minWidth: '70px',
    flexShrink: 0,
    color: '#829ab1',
    fontSize: '9px',
    fontWeight: '800',
    letterSpacing:
      '0.07em',
    textTransform:
      'uppercase'
  },

  description: {
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#243b53',
    margin: '6px 0'
  },

  readingTitle: {
    border: 'none',
    background: 'none',
    color: '#2f6f9f',
    fontFamily:
      'inherit',
    fontSize: 'inherit',
    lineHeight:
      'inherit',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration:
      'underline',
    padding: 0,
    textAlign: 'left'
  },

  previewOverlay: {
    position: 'fixed',
    inset: 0,
    background:
      'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems:
      'center',
    justifyContent:
      'center',
    zIndex: 300,
    padding: '20px'
  },

  previewPanel: {
    background:
      'white',
    borderRadius:
      '6px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection:
      'column',
    overflow: 'hidden'
  },

  previewHeader: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems:
      'flex-start',
    padding:
      '16px 20px',
    borderBottom:
      '1px solid #d9e2ec'
  },

  previewTitle: {
    fontWeight: '700',
    fontSize: '16px',
    color: '#243b53'
  },

  previewAuthor: {
    fontSize: '13px',
    color: '#627d98',
    marginTop: '2px'
  },

  previewClose: {
    border: 'none',
    background: 'none',
    fontSize: '26px',
    cursor: 'pointer',
    color: '#627d98',
    lineHeight: 1
  },

  previewFrame: {
    flex: 1,
    border: 'none',
    minHeight: '500px'
  },

  previewNoFile: {
    padding: '20px',
    color: '#627d98',
    fontSize: '14px'
  },

  downloadLink: {
    display: 'block',
    padding:
      '12px 20px',
    color: '#2f6f9f',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration:
      'underline',
    borderTop:
      '1px solid #d9e2ec'
  }
};
